import { Building2, Users } from "lucide-react";
import { SelectableCard } from "../SelectableCard";
import type { WizardAction } from "../types";
import type { Dispatch } from "../hooks/useWizardEntity";

interface Step0Props {
  dispatch: Dispatch<WizardAction>;
  /** Current answer, so re-visiting this step via Back shows the prior choice highlighted. */
  isExternal: boolean;
}

/**
 * Step 1 — own vs. external, asked before the URL step (step 2). Client-side flow
 * control only: it decides which Step 3 role options to show and whether Steps 4-7
 * (Goals/Build scope/Stage/First task) are skipped, but is never sent to the backend
 * as its own field. Own-vs-external actually persists via whichever relationship_type
 * Step 3 resolves to (see roleConfig.EXTERNAL_ROLE_OPTIONS + handleBuild in
 * Wizard_Entity_Onboarding).
 *
 * Choosing a card both sets the answer and advances — mirrors how Step 2 (URL extract)
 * owns its own "Continue" rather than relying on the shared wizard footer. The user can
 * come back here via the URL step's Back button and change their answer at any time.
 */
export function Step0EntityRelation({ dispatch, isExternal }: Step0Props) {
  const choose = (nextIsExternal: boolean) => {
    dispatch({ type: "SET_IS_EXTERNAL", value: nextIsExternal });
    dispatch({ type: "SET_STEP", step: 2, direction: 1 });
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-14 flex flex-col gap-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-foreground tracking-tight">
          Whose entity is this?
        </h1>
        <p className="text-lg text-muted-foreground/70 mt-2 leading-relaxed">
          This decides what Raisy asks you next.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <SelectableCard
          layout="row"
          selected={!isExternal}
          onClick={() => choose(false)}
          icon={<Building2 className="h-4 w-4" />}
          label="My own entity"
          description="A few quick questions about your role & goals"
          tooltip={
            <>
              <p className="font-medium mb-0.5">Your own entity</p>
              <p className="text-sm leading-relaxed opacity-90">
                A company, product, or person that belongs to you or your organisation.
                You'll answer a few quick questions about your role and goals so Raisy
                can build around it.
              </p>
            </>
          }
        />
        <SelectableCard
          layout="row"
          selected={isExternal}
          onClick={() => choose(true)}
          icon={<Users className="h-4 w-4" />}
          label="External entity"
          description="Just confirm how you relate to them"
          tooltip={
            <>
              <p className="font-medium mb-0.5">External entity</p>
              <p className="text-sm leading-relaxed opacity-90">
                A third party — a client, prospect, partner, or vendor. Raisy just
                scrapes and stores what's public online; you'll just confirm how you
                relate to them.
              </p>
            </>
          }
        />
      </div>
    </div>
  );
}
